import { useState } from "react";
import { useStore } from "./store";
import { X, CheckCircle, AlertCircle, Loader2, Workflow, Link as LinkIcon, FileText } from "lucide-react";

const ResultModal = ({ isOpen, onClose, result, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all animate-in zoom-in-95 duration-200 border border-gray-100 dark:border-gray-700">

        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-purple-600 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-full p-1"
          >
            <X size={20} />
          </button>

          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Workflow size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Pipeline Analysis</h2>
              <p className="text-white/80 text-sm">Submission successful</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-primary/20 transition-colors group">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                <FileText size={16} />
                <span className="text-xs font-semibold uppercase tracking-wider">Nodes</span>
              </div>
              <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 group-hover:text-primary transition-colors">
                {result.num_nodes}
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-primary/20 transition-colors group">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                <LinkIcon size={16} />
                <span className="text-xs font-semibold uppercase tracking-wider">Edges</span>
              </div>
              <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 group-hover:text-primary transition-colors">
                {result.num_edges}
              </p>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/50 flex items-center justify-between">
            <div>
              <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 block">DAG Status</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Directed Acyclic Graph</span>
            </div>

            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border ${result.is_dag
              ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800"
              : "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800"
              }`}>
              {result.is_dag ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              {result.is_dag ? "Active" : "Invalid"}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-200 text-white dark:text-gray-900 font-medium rounded-lg transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export const SubmitButton = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [pipelineResult, setPipelineResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    const { nodes, edges } = useStore.getState();
    setIsLoading(true);
    try {
      // Optimize payload: only send necessary data
      const response = await fetch("http://localhost:8000/pipelines/parse", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          nodes: nodes.map((node) => ({ id: node.id })),
          edges: edges.map((edge) => ({
            source: edge.source,
            target: edge.target,
          })),
        }),
      });

      const result = await response.json();
      setPipelineResult(result);
      setModalOpen(true);
    } catch (error) {
      console.error(error);
      alert("Backend connection failed"); // Fallback for network error
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="absolute top-4 right-4 md:top-8 md:right-8 z-20 transition-all duration-300">
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="
            bg-primary 
            hover:bg-primary-hover 
            disabled:bg-primary/70
            disabled:cursor-not-allowed
            text-white 
            font-semibold 
            py-2 px-4
            md:py-3 md:px-8 
            text-sm md:text-base
            rounded-xl 
            shadow-lg 
            hover:shadow-glow 
            transform 
            hover:-translate-y-0.5 
            transition-all 
            duration-200
            flex
            items-center
            gap-2
            border-none
            outline-none
            ring-offset-2
            focus:ring-2
            focus:ring-primary
            dark:ring-offset-dark-bg
            group
          "
        >
          {isLoading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <span className="relative flex h-3 w-3 mr-1">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white/80"></span>
            </span>
          )}
          {isLoading ? "Analyzing..." : "Submit Pipeline"}
        </button>
      </div>

      <ResultModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        result={pipelineResult || { num_nodes: 0, num_edges: 0, is_dag: false }}
      />
    </>
  );
};

